import classnames from "classnames";
import PropTypes from "prop-types";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useFieldArray, useFormContext, useWatch } from "react-hook-form";
import { shallow } from "zustand/shallow";

import { getFormattedDate, limitDecimal, toDollar } from "../../../helpers";
import useAdminAddUsersFormStore from "../../../store/AdminAddUsersForm.store";
import useIntakeFormStore from "../../../store/IntakeForm.store";

import useUserStore from "../../../store/User.store";
import Alert from "../../Alert";
import Button from "../../Button";
import Modal from "../../Modal";
import Tooltip from "../../Tooltip";
import FileUploader from "../FileUploader";
import GroupField from "../GroupField";

import { fetcher } from "../../../apiHelpers";

// get the next available button from the list
const getNextAvailableButton = (list = [], index = 0) => {
  const totalList = list.filter(Boolean).length - 1;
  const nextIndex = totalList === index ? index - 1 : index + 1;
  return list[nextIndex];
};

const DynamicGroupField = ({
  title,
  fieldArrayName,
  inputFields,
  tooltip,
  disabled,
  store,
  displayTotal,
  isRequired,
  isTest,
  importantMessage,
  className,
  quantityUnitLabel = "Quantity / Units",
}) => {
  const { formState: adminAddUsersFormState } = useAdminAddUsersFormStore(
    (state) => state.data.formState
  );
  const { handleSetAdminAddUsersFormStates } =
    useAdminAddUsersFormStore.getState().actions;

  const intakeFormState = useIntakeFormStore(
    (state) => state.data.formState,
    shallow
  );
  const { handleSetIntakeFormStates } = useIntakeFormStore.getState().actions;

  let formState, handleSetFormStates;

  if (store === "adminAddUsers") {
    formState = adminAddUsersFormState;
    handleSetFormStates = handleSetAdminAddUsersFormStates;
  } else {
    formState = intakeFormState;
    handleSetFormStates = handleSetIntakeFormStates;
  }

  const [showConfirmAlert, setShowConfirmAlert] = useState(false);
  const [showDeleteAllRowsModal, setShowDeleteAllRowsModal] = useState(false);
  const [showDeleteRowModal, setShowDeleteRowModal] = useState(false);
  const [rowIndexToDelete, setRowIndexToDelete] = useState(null);
  const [elementToFocus, setElementToFocus] = useState(null);
  const [totalCostBasis, setTotalCostBasis] = useState(0);
  const [totalQty, setTotalQty] = useState(0);
  const [totalSecuritiesCount, setTotalSecuritiesCount] = useState(0);

  const hasUploadedFlagRef = useRef(false);
  const uploadTimeout = useRef(null);
  const scrollContainerRef = useRef(null);
  const currentRowsLengthRef = useRef(0);
  const csvLength = useRef(0);
  const csvFileName = useRef(null);
  const csvHasError = useRef(false);
  const csvErrorRowsLength = useRef(0);
  const focusIndexRef = useRef(null);
  const addNewRowRef = useRef(null);
  const removeRowRefs = useRef([]);

  const { user } = useUserStore().data;
  const token = user?.accessToken;

  const {
    control,
    formState: { errors },
  } = useFormContext();
  const { fields, append, remove } = useFieldArray({
    control,
    name: fieldArrayName,
  });
  const fieldArrayValues = useWatch({
    control,
    name: fieldArrayName,
  });

  const fieldsArrayObj = useMemo(() => {
    return {};
  }, []);
  inputFields.forEach((field) => (fieldsArrayObj[field.name] = ""));

  const handleAddRow = useCallback(() => {
    append(fieldsArrayObj, true);
    currentRowsLengthRef.current = currentRowsLengthRef.current + 1;
  }, [append, fieldsArrayObj]);

  const handleRemoveAll = () => {
    focusIndexRef.current = null;
    remove();

    hasUploadedFlagRef.current = false;
    showConfirmAlert && setShowConfirmAlert(false);
    csvFileName.current = null;
    csvLength.current = 0;
    csvErrorRowsLength.current = 0;
    csvHasError.current = false;

    currentRowsLengthRef.current = 0;

    // Reset Store data
    handleSetFormStates({
      [fieldArrayName]: {
        csvFileName: null,
        csvLength: 0,
        csvErrorRowsLength: 0,
        totalErrorCount: 0,
      },
    });

    setShowDeleteAllRowsModal(false);
    setElementToFocus(addNewRowRef.current);
    handleAddRow();
  };

  const handleRemoveOne = (idx) => {
    setRowIndexToDelete(idx);

    if (showDeleteRowModal) {
      // store the current index before it gets removed
      const currentIndex = rowIndexToDelete;

      // get the next avaialable button
      const nextRemoveButton = getNextAvailableButton(
        removeRowRefs.current,
        currentIndex
      );

      // move the focus to the next available remove button
      // focusToButton(nextRemoveButton);
      setElementToFocus(nextRemoveButton);

      remove(rowIndexToDelete);
      setRowIndexToDelete(null);
      setShowDeleteRowModal(false);
      return;
    }

    setShowDeleteRowModal(true);
  };

  const handleValidateRows = useCallback(() => {
    // Get total errors in all fields
    const fieldErrors = errors[fieldArrayName]?.filter((e) => e);
    const totalFieldErrorsCount = fieldErrors?.length;

    // Get total errors from the uploaded CSV rows
    if (hasUploadedFlagRef.current || csvErrorRowsLength.current) {
      // 1. Create an array of indexes from the existing row fields prior CSV upload
      const fieldsIndexArray = Array.from(
        { length: currentRowsLengthRef.current },
        (v, k) => k++
      );
      // 2. Filter the error array with the matching indexes from `fieldsIndexArray`
      const uploadedCSVError = errors[fieldArrayName]?.filter((e, i) => {
        return e && fieldsIndexArray?.indexOf(i) === -1;
      });
      const uploadCSVErrorCount = uploadedCSVError?.length;

      csvErrorRowsLength.current = uploadCSVErrorCount;
      csvHasError.current = !!uploadCSVErrorCount;

      setShowConfirmAlert(true);
    }

    // Set Error info into Store
    const { formState } = useIntakeFormStore.getState().data;
    handleSetFormStates({
      [fieldArrayName]: {
        ...formState?.[fieldArrayName],
        csvErrorRowsLength: csvErrorRowsLength.current || 0,
        totalErrorCount: totalFieldErrorsCount || 0,
      },
    });
  }, [errors, fieldArrayName, csvHasError, handleSetFormStates]);

  const handleRemoveCleanRows = () => {
    const cleanRows = scrollContainerRef.current.querySelectorAll(
      ".form-section__content-row > .js-is-clean"
    );
    const rowIndexes = Array.from(cleanRows).map(
      (row) => row.parentNode.dataset.rowIndex
    );
    currentRowsLengthRef.current = fields.length - rowIndexes.length;
    remove(rowIndexes);
  };

  const handleAppendNewRow = (data) => {
    // Map Field Object to CSV data
    const newRows = [];

    data.map((d) => {
      const newFieldObj = Object.keys(fieldsArrayObj)
        .map((k, i) => ({
          [k]: d[i],
        }))
        .reduce((obj, item) => {
          const [key, value] = Object.entries(item)[0];
          let thisValue = value;
          if (key === "purchaseDate") {
            thisValue = getFormattedDate(value);
          }
          return { ...obj, [key]: thisValue };
        });
      newRows.push(newFieldObj);
      return () => {};
    });

    // Append function to new rows
    append(newRows);
  };

  const handleUpload = (data, file) => {
    uploadTimeout.current && clearTimeout(uploadTimeout.current);

    // Reset CSV Length
    csvLength.current = 0;

    // Reset Input File Uploader
    document.getElementById(`csvFileUploader_${fieldArrayName}`).value = "";

    // Check for empty rows before adding new ones
    handleRemoveCleanRows();

    // Read CSV data, transform to array and append to fields array
    handleAppendNewRow(data);

    // Read CSV data and get the length and filename
    csvLength.current = data.length;
    csvFileName.current = file.name;

    // Show Confirmation ALert
    hasUploadedFlagRef.current = true;

    // Store Uploaded CSV info into the Store
    handleSetFormStates({
      [fieldArrayName]: {
        ...formState[fieldArrayName],
        csvFileName: file.name,
        csvLength: data.length,
      },
    });

    handleValidateRows();
  };

  // Add Gradient to indicate scrollable content
  const handleScrollGradient = () => {
    const divScroller = scrollContainerRef.current;
    if (
      divScroller.offsetHeight + divScroller.scrollTop >=
      divScroller.scrollHeight
    ) {
      divScroller.classList.add("has-scrolled-bottom");
    } else {
      divScroller.classList.remove("has-scrolled-bottom");
    }
  };

  // Get Field Array Values Count
  const getFieldArrayValuesCount = useCallback((data) => {
    if (!data || data?.length < 1) return 0;

    return data.filter((item) => item.cusip && item.cusip !== "").length;
  }, []);

  useEffect(() => {
    // Set Refs values if Store>formState is available,
    // and has error/s recorded from previously uploaded CSV
    if (formState && formState?.[fieldArrayName]?.csvErrorRowsLength > 0) {
      const fs = formState[fieldArrayName];
      setShowConfirmAlert(true);

      csvLength.current = fs.csvLength;
      csvFileName.current = fs.csvFileName;
      csvErrorRowsLength.current = fs.csvErrorRowsLength;
      csvHasError.current = true;
    }

    handleValidateRows();

    setTotalCostBasis(
      fieldArrayValues?.reduce((total, field) => {
        return total + field.costBasis;
      }, 0)
    );

    setTotalQty(
      fieldArrayValues?.reduce((total, field) => {
        return total + field.qty;
      }, 0)
    );

    setTotalSecuritiesCount(getFieldArrayValuesCount(fieldArrayValues));

    // Set timer to remove the alert if no errors
    if (
      (hasUploadedFlagRef.current && !csvHasError.current) ||
      !csvHasError.current
    ) {
      uploadTimeout.current = setTimeout(() => {
        setShowConfirmAlert(false);
        hasUploadedFlagRef.current = false;
      }, 4000);
    }
    return () => {
      csvErrorRowsLength.current = 0;

      uploadTimeout.current && clearTimeout(uploadTimeout.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fields, fieldArrayValues, errors, fieldArrayName]);

  useEffect(() => {
    const divScroller = scrollContainerRef.current;
    if (divScroller) {
      const scrollHeight = divScroller.scrollHeight;

      if (scrollHeight > 560) {
        divScroller.classList.add("is-scrollable");
      } else {
        divScroller.classList.remove("is-scrollable");
      }

      divScroller.addEventListener("scroll", handleScrollGradient);
    }

    return () => {
      divScroller.removeEventListener("scroll", handleScrollGradient);
    };
  }, [fields]);

  useEffect(() => {
    if (isTest) handleAddRow();
  }, [isTest, handleAddRow]);

  const addNewRowText = "Add New Row";

  return (
    <div className={classnames("form-section", className)} aria-live="polite">
      {fieldArrayName !== "securityLimitData" && (
        <div className="form-section__header">
          <h3 className="form-section__title">{title}</h3>
          {tooltip && <Tooltip {...tooltip} />}
          {isRequired && (
            <div className="form-section__title-required">
              <span className="form-section__title-required-asterisk">*</span>
              <span className="form-section__title-required-label">
                Required Fields
              </span>
            </div>
          )}
        </div>
      )}
      {importantMessage}
      <div className="form-section__header">
        {store !== "adminAddUsers" && (
          <div className="form-section__header-total">
            {totalSecuritiesCount} Total Securities
          </div>
        )}
        {!disabled && (
          <div className="form-section__header-control">
            <Button
              ref={addNewRowRef}
              type="button"
              variant="link"
              color="normal"
              size="small"
              iconName="system-add"
              onClick={handleAddRow}
            >
              {addNewRowText}
            </Button>
            <Button
              type="button"
              variant="link"
              color="normal"
              size="small"
              iconName="system-trash"
              onClick={() => setShowDeleteAllRowsModal(!disabled)}
              disabled={fields.length <= 1 || disabled}
            >
              Delete All
            </Button>
          </div>
        )}
      </div>

      {showConfirmAlert && (
        <Alert type={!csvHasError.current ? "success" : "error"}>
          {!csvHasError.current ? (
            <>
              You have successfully imported {csvLength.current} rows from{" "}
              <b>{csvFileName.current}</b>
            </>
          ) : (
            <>
              You have errors in {csvErrorRowsLength.current} out of{" "}
              {csvLength.current} rows from <b>{csvFileName.current}</b>
            </>
          )}
        </Alert>
      )}

      <div
        className="form-section__content scrollable-container"
        ref={scrollContainerRef}
      >
        {fields.map(({ id }, index) => {
          const fieldName = `${fieldArrayName}[${index}]`;

          return (
            <div
              className="form-section__content-row"
              data-row-index={index}
              key={id}
            >
              <GroupField
                name={fieldName}
                fieldArrayName={fieldArrayName}
                fieldIndex={index}
                inputFields={inputFields}
                focusIndexRef={focusIndexRef}
                elementToFocus={elementToFocus}
                disabled={disabled}
              />
              <div className="form-section__content-row-control">
                <Button
                  type="button"
                  ref={(element) => {
                    removeRowRefs.current[index] = element;
                  }}
                  variant="link"
                  size="small"
                  color="normal"
                  iconName="system-trash"
                  aria-label="Remove"
                  onClick={() => handleRemoveOne(index)}
                  disabled={disabled}
                />
              </div>
            </div>
          );
        })}
        {displayTotal && (
          <div className="form-section__content-row-total-grid">
            <div className="form-section__content-row-total">
              <div className="total">
                <div className="label total">Totals</div>
                <div className="value">{totalSecuritiesCount} Securities</div>
              </div>
              <div className="cost">
                <div className="label">Adjusted cost basis</div>
                <div className="value">{toDollar(totalCostBasis || 0)}</div>
              </div>
              <div className="quantity">
                <div className="label">{quantityUnitLabel}</div>
                <div className="value">{limitDecimal(totalQty || 0)}</div>
              </div>
            </div>
          </div>
        )}
      </div>
      {fieldArrayName !== "securityLimitData" && !disabled && (
        <div className="form-section__footer">
          <FileUploader
            name={`csvFileUploader_${fieldArrayName}`}
            label="Upload From CSV"
            onUpload={handleUpload}
            disabled={disabled}
          />
          <Button
            className="guide-to-uploading"
            variant="outline"
            type="button"
            onClick={() => fetcher({ url: "guide", token })}
            iconName="system-arrow-up-right"
            iconPosition="right"
            iconSize="small"
            aria-label="Guide to Uploading"
          >
            Guide to Uploading
          </Button>
        </div>
      )}
      <Modal
        variation="basic"
        show={showDeleteAllRowsModal}
        onClose={() => setShowDeleteAllRowsModal(false)}
        onConfirm={handleRemoveAll}
        heading="Delete All Rows?"
        subtitle="Are you sure you want to delete all the rows? This action is undoable."
        cancelButton={{
          variant: "outline",
          children: "Cancel",
        }}
        confirmButton={{
          variant: "danger",
          children: "Delete All Rows",
        }}
      />
      <Modal
        variation="basic"
        show={showDeleteRowModal}
        onClose={() => setShowDeleteRowModal(false)}
        onConfirm={handleRemoveOne}
        heading="Delete Row?"
        subtitle="Are you sure you want to delete this row? This action is undoable."
        cancelButton={{
          variant: "outline",
          children: "Cancel",
        }}
        confirmButton={{
          variant: "danger",
          children: "Delete Row",
        }}
      />
    </div>
  );
};

DynamicGroupField.propTypes = {
  title: PropTypes.string,
  fieldArrayName: PropTypes.string.isRequired,
  inputFields: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      options: PropTypes.object,
    })
  ).isRequired,
  tooltip: PropTypes.shape({
    heading: PropTypes.string.isRequired,
    content: PropTypes.node.isRequired,
    direction: PropTypes.string.isRequired,
  }),
  disabled: PropTypes.bool,
  store: PropTypes.oneOf(["intake", "adminAddUsers"]).isRequired,
  displayTotal: PropTypes.bool,
  isRequired: PropTypes.bool,
  importantMessage: PropTypes.node,
  className: PropTypes.string,
  isTest: PropTypes.bool,
};

DynamicGroupField.defaultProps = {
  isRequired: false,
};

export default DynamicGroupField;
